import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { alpha } from '@mui/material/styles';
import { getTileModifierMeta } from './boardConfig';
import { RARITY_ORDER, getRarity } from './config/rarityConfig';

function LetterTile({
  tile,
  dragData,
  draggable = true,
  compact = false,
  variant = 'rack',
  isHoveringBoard = false,
  boardSquareModifierCount = 0,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrSmaller = useMediaQuery('(max-width:1080px)');
  const isBoardVariant = variant === 'board';
  const isOverlayVariant = variant === 'overlay';
  const isInteractiveDrag = draggable && !isOverlayVariant;
  const shouldUseModifierDots = isBoardVariant && compact && isTabletOrSmaller;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: isOverlayVariant ? `overlay-${tile.id}` : tile.id,
    data: dragData,
    disabled: !isInteractiveDrag,
  });

  const overlayTransformScale = isOverlayVariant ? (isHoveringBoard ? 0.92 : 1) : 1;
  const translateValue = isOverlayVariant && transform ? CSS.Translate.toString(transform) : '';
  const composedTransform = `${translateValue}${overlayTransformScale !== 1 ? ` scale(${overlayTransformScale})` : ''}`.trim();

  const style = isOverlayVariant && transform
    ? {
        transform: composedTransform,
        willChange: 'transform',
      }
    : isOverlayVariant
      ? {
          transform: composedTransform || `scale(${overlayTransformScale})`,
          willChange: 'transform',
        }
      : undefined;

  const dragInteractionProps = isInteractiveDrag
    ? {
        ...listeners,
        ...attributes,
      }
    : {};

  const tileModifiers = tile?.modifiers;
  const tileScoreMultiplier =
    Number(tileModifiers?.multiplier) > 1 ? Number(tileModifiers.multiplier) : null;
  const tileScoreBonus = Number(tileModifiers?.score) || 0;
  const tileExtras = Array.isArray(tileModifiers?.extras) ? tileModifiers.extras : [];

  // Rarity coloring: look up each applied modifier ID's rarity directly
  const tileIds = Array.isArray(tileModifiers?.ids) ? tileModifiers.ids : [];
  const allModifierIds = [...new Set([...tileIds, ...tileExtras])];
  let highestRarityIndex = -1;
  allModifierIds.forEach((modifierId) => {
    try {
      const rarity = getTileModifierMeta(modifierId)?.rarity || 'common';
      const idx = RARITY_ORDER.indexOf(rarity);
      if (idx > highestRarityIndex) highestRarityIndex = idx;
    } catch { /* ignore unknown */ }
  });
  const tileHighestRarity = highestRarityIndex >= 0 ? RARITY_ORDER[highestRarityIndex] : null;
  const rarityColor = tileHighestRarity ? getRarity(tileHighestRarity).color : null;
  const isLegendaryTile = tileHighestRarity === 'legendary';

  const extrasMetaList = tileExtras.map((modifierId) => {
    try {
      const meta = getTileModifierMeta(modifierId);
      return {
        id: modifierId,
        badge: meta.badge || modifierId,
        scoreBonus: Number(meta.scoreBonus || 0),
        collapseIntoScoreDisplay: Boolean(meta.collapseIntoScoreDisplay),
      };
    } catch {
      return { id: modifierId, badge: modifierId, scoreBonus: 0, collapseIntoScoreDisplay: false };
    }
  });

  const scoreBonusTotal =
    tileScoreBonus +
    extrasMetaList.reduce((total, modifier) => total + Number(modifier.scoreBonus || 0), 0);
  const basePoints = Number(tile?.points ?? 0);
  const displayPoints = Math.max(0, Math.ceil(basePoints + scoreBonusTotal));
  const hasScoreIncrease = displayPoints > basePoints;
  const hasScoreDecrease = displayPoints < basePoints;
  const scoreBadgeColors = hasScoreIncrease
    ? {
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.72)} 0%, ${alpha(
          theme.palette.success.light,
          0.8
        )} 100%)`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
      }
    : hasScoreDecrease
      ? {
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.68)} 0%, ${alpha(
            theme.palette.error.light,
            0.78
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.error.main, 0.8)}`,
        }
      : {
          background: `linear-gradient(135deg, ${alpha(theme.palette.scrabble.tileText, 0.18)} 0%, ${alpha(
            theme.palette.scrabble.tileText,
            0.26
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.scrabble.tileText, 0.34)}`,
        };

  // Extras that are visible as badges (not collapsed into score display)
  const extrasVisible = extrasMetaList.filter((m) => !m.collapseIntoScoreDisplay);
  const extrasCount = extrasVisible.length;

  // All variants: multiplier top-left, extras bottom-left
  const showMultiplierBadge = tileScoreMultiplier !== null;
  const showExtrasBadge = extrasCount > 0;
  const extrasBadgeContent = extrasCount === 1 ? extrasVisible[0].badge : String(extrasCount);

  const hasBoardSquareModifierBadge =
    isBoardVariant && Number(boardSquareModifierCount || 0) > 0;
  const showCompactScoreBadge = !isMobile || !isBoardVariant;
  const badgeTextColor =
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.95) : theme.palette.scrabble.tileText;
  const boardModifierDotCount = Math.min(3, Math.max(0, Number(boardSquareModifierCount || 0)));

  const baseBadgeSx = {
    fontWeight: 800,
    fontSize: compact ? 7.5 : 9,
    lineHeight: 1,
    color: badgeTextColor,
    borderRadius: compact ? 0.4 : 0.6,
    paddingX: compact ? 0.44 : 0.58,
    paddingY: compact ? 0.18 : 0.24,
    maxWidth: compact ? 30 : 46,
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.24)}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // Green — multiplier (top-left, all variants)
  const tileModifierBadgeSx = {
    ...baseBadgeSx,
    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.58)} 0%, ${alpha(
      theme.palette.success.light,
      0.7
    )} 100%)`,
    border: `1px solid ${alpha(theme.palette.success.main, 0.62)}`,
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.24)}, 0 1px 2px ${alpha(
      theme.palette.success.main,
      0.26
    )}`,
  };

  // Purple — extras modifiers (bottom-left, all variants)
  const extrasModifierBadgeSx = {
    ...baseBadgeSx,
    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.55)} 0%, ${alpha(
      theme.palette.secondary.light,
      0.68
    )} 100%)`,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.24)}, 0 1px 2px ${alpha(
      theme.palette.secondary.main,
      0.24
    )}`,
  };

  // Blue — board square modifiers (board top-right)
  const boardModifierBadgeSx = {
    ...baseBadgeSx,
    background: `linear-gradient(135deg, ${alpha(theme.palette.scrabble.tl, 0.55)} 0%, ${alpha(
      theme.palette.scrabble.dl,
      0.7
    )} 100%)`,
    border: `1px solid ${alpha(theme.palette.scrabble.tl, 0.62)}`,
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.24)}, 0 1px 2px ${alpha(
      theme.palette.scrabble.tl,
      0.26
    )}`,
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...style,
        aspectRatio: '1 / 1',
        borderRadius: isBoardVariant || isOverlayVariant ? 0.5 : 0.75,
        width: isOverlayVariant ? '48px' : '100%',
        height: isOverlayVariant ? '48px' : '100%',
        backgroundColor: theme.palette.scrabble.tile,
        color: theme.palette.scrabble.tileText,
        border: rarityColor
          ? `1px solid ${alpha(rarityColor, 0.72)}`
          : `1px solid ${alpha(theme.palette.scrabble.tileText, 0.22)}`,
        boxShadow: isDragging
          ? 3
          : rarityColor
            ? `0 1px 0 ${alpha(rarityColor, 0.42)}, 0 ${isBoardVariant ? 4 : 3}px ${isBoardVariant ? 8 : 6}px ${alpha(rarityColor, 0.38)}`
            : isBoardVariant
              ? `0 1px 0 ${alpha(theme.palette.scrabble.tileText, 0.2)}, 0 4px 6px ${alpha(theme.palette.scrabble.tileText, 0.16)}`
              : `0 1px 0 ${alpha(theme.palette.scrabble.tileText, 0.24)}, 0 3px 5px ${alpha(theme.palette.scrabble.tileText, 0.14)}`,
        opacity: isDragging && !isOverlayVariant ? 0 : 1,
        cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
        display: 'grid',
        gridTemplateRows: '1fr',
        placeItems: 'center',
        userSelect: 'none',
        touchAction: isInteractiveDrag ? 'none' : 'auto',
        zIndex: isOverlayVariant ? 4 : isBoardVariant ? 2 : 1,
        position: 'relative',
        overflow: 'hidden',
        transformOrigin: 'center center',
        willChange: isDragging || isOverlayVariant ? 'transform, opacity' : 'auto',
        transition: isDragging
          ? 'none'
          : 'transform 90ms ease-out, box-shadow 140ms ease, opacity 120ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: rarityColor
            ? `linear-gradient(145deg, ${alpha('#fff', 0.42)} 0%, ${alpha('#fff', 0)} 45%), linear-gradient(160deg, ${alpha(rarityColor, 0.36)} 0%, ${alpha(rarityColor, 0.22)} 100%)`
            : `linear-gradient(145deg, ${alpha('#fff', 0.42)} 0%, ${alpha('#fff', 0)} 45%)`,
          pointerEvents: 'none',
        },
        '&::after': isLegendaryTile ? {
          content: '""',
          position: 'absolute',
          top: -2,
          bottom: -2,
          right: '-28%',
          width: '22%',
          transform: 'skewX(-17deg)',
          pointerEvents: 'none',
          background: `linear-gradient(110deg, ${alpha('#ff9800', 0)} 0%, ${alpha('#ffffff', 0.58)} 48%, ${alpha('#ff9800', 0)} 100%)`,
          animation: 'tileLegendaryShine 2.6s ease-in-out infinite',
        } : undefined,
        '@keyframes tileLegendaryShine': {
          '0%': { right: '-28%', opacity: 0 },
          '10%': { opacity: 1 },
          '34%': { right: '110%', opacity: 0 },
          '100%': { right: '110%', opacity: 0 },
        },
      }}
      {...dragInteractionProps}
      aria-label={`Tile ${tile.letter}`}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: compact
            ? (isBoardVariant || isOverlayVariant ? (isMobile ? 17.5 : 16.5) : 18.5)
            : 22,
          lineHeight: 1,
          textShadow: `0 1px 0 ${alpha('#fff', 0.5)}`,
        }}
      >
        {tile.letter}
      </Typography>

      {/* Top-left: multiplier badge (all variants) */}
      {showMultiplierBadge ? (
        <Box
          sx={{
            position: 'absolute',
            top: 1.5,
            left: 1.5,
            pointerEvents: 'none',
          }}
        >
          {shouldUseModifierDots ? (
            <Box
              sx={{
                width: 5.5,
                height: 5.5,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.success.main, 0.95),
                boxShadow: `0 0 0 1px ${alpha(theme.palette.background.paper, 0.8)}`,
              }}
            />
          ) : (
            <Box sx={tileModifierBadgeSx}>
              {`x${tileScoreMultiplier}`}
            </Box>
          )}
        </Box>
      ) : null}

      {/* Top-right: board square modifier count (board only) */}
      {hasBoardSquareModifierBadge ? (
        <Box
          sx={{
            position: 'absolute',
            top: 1.5,
            right: 1.5,
            pointerEvents: 'none',
          }}
        >
          {shouldUseModifierDots ? (
            <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
              {Array.from({ length: boardModifierDotCount }).map((_, index) => (
                <Box
                  key={`${tile.id}-board-dot-${index + 1}`}
                  sx={{
                    width: 5.5,
                    height: 5.5,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.scrabble.tl, 0.95),
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.background.paper, 0.8)}`,
                  }}
                />
              ))}
            </Box>
          ) : (
            <Box sx={boardModifierBadgeSx}>
              {String(boardSquareModifierCount)}
            </Box>
          )}
        </Box>
      ) : null}

      {/* Bottom-left: extras modifier badge (all variants) */}
      {showExtrasBadge ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: 1.5,
            left: 1.5,
            pointerEvents: 'none',
          }}
        >
          {shouldUseModifierDots ? (
            <Box sx={{ display: 'flex', gap: 0.3 }}>
              {Array.from({ length: Math.min(3, extrasCount) }).map((_, index) => (
                <Box
                  key={`${tile.id}-extras-dot-${index + 1}`}
                  sx={{
                    width: 5.5,
                    height: 5.5,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.secondary.main, 0.95),
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.background.paper, 0.8)}`,
                  }}
                />
              ))}
            </Box>
          ) : (
            <Box sx={extrasModifierBadgeSx}>
              {extrasBadgeContent}
            </Box>
          )}
        </Box>
      ) : null}

      {/* Bottom-right: score value */}
      {!compact ? (
        <Box
          sx={{
            position: 'absolute',
            right: 4,
            bottom: 4,
            borderRadius: 0.8,
            paddingX: 0.5,
            paddingY: 0.18,
            lineHeight: 1,
            color: badgeTextColor,
            boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.28)}`,
            ...scoreBadgeColors,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 13.5,
              lineHeight: 1,
              color: 'inherit',
              textShadow: `0 1px 0 ${alpha('#fff', 0.44)}`,
            }}
          >
            {displayPoints}
          </Typography>
        </Box>
      ) : tile.points != null && showCompactScoreBadge ? (
        <Box
          sx={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            borderRadius: 0.6,
            paddingX: 0.38,
            paddingY: 0.14,
            lineHeight: 1,
            color: badgeTextColor,
            boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.background.paper, 0.24)}`,
            ...scoreBadgeColors,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 11,
              lineHeight: 1,
              color: 'inherit',
              textShadow: `0 1px 0 ${alpha('#fff', 0.4)}`,
            }}
          >
            {displayPoints}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export default LetterTile;
